'use strict';

import { environment } from '@quartz-gov-legacy/environments/environment';
import { MonitoringUtils } from '@quartz-gov-legacy/app/monitoring/monitoringUtils';

/**
 * Keycloak authentication provider for AngularJS.
 * Provides authentication functionality compatible with the authService facade.
 *
 * Shared across QuartzX legacy projects. Keep in sync with QuartzX.Web.IAM.Legacy.
 *
 * @module services.keycloakProvider
 */
angular.module('services.keycloakProvider', ['services.lazyLoadScript'])

.factory('keycloakProvider', ['$q', '$interval', '$rootScope', '$window', '$injector', 'lazyLoadScriptService',
  function($q, $interval, $rootScope, $window, $injector, lazyLoadScriptService) {

  var service = {};

  // Keycloak instance
  var keycloak = null;
  var keycloakReady = false;

  // Initialization state tracking
  var initPromise = null;
  var initState = 'pending'; // 'pending' | 'ready' | 'failed'

  // Authentication state
  var authState = {
    isAuthenticated: false,
    userProfile: null,
    accessToken: null,
  };

  // Token refresh interval handle
  var refreshIntervalHandle = null;

  // Login circuit breaker — prevents infinite re-auth loops when the IdP keeps
  // rejecting refresh attempts (network blip, expired SSO session, mid-flight
  // misconfig, etc). Tracks recent login() calls in sessionStorage so the
  // counter survives page reloads triggered by SAML round-trips.
  var LOGIN_ATTEMPTS_STORAGE_KEY = 'kc_login_attempts';
  var LOGIN_ATTEMPTS_MAX = 3;
  var LOGIN_ATTEMPTS_WINDOW_MS = 60000;

  // Re-entry guard for _handleAuthLost. The recovery flow defers via
  // visibilitychange/online listeners, and overlapping recoveries would race.
  var _authLostHandling = false;

  /**
   * Initialize Keycloak with configuration.
   * Idempotent - returns existing promise if already initializing.
   *
   * @param {object} options - Optional initialization options
   * @param {boolean} options.forceLogin - Force immediate login redirect instead of check-sso
   * @returns {Promise<boolean>} Promise that resolves to true if authenticated
   */
  service.init = function(options) {
    options = options || {};

    if (initState === 'ready' || initState === 'failed') {
      return initPromise;
    }

    if (initState === 'pending' && initPromise) {
      return initPromise;
    }

    var deferred = $q.defer();
    initPromise = deferred.promise;
    initState = 'pending';

    if (!environment.keycloak) {
      console.error('[keycloakProvider] Keycloak configuration missing in environment');
      initState = 'failed';
      deferred.reject(new Error('Keycloak configuration missing'));
      return deferred.promise;
    }

    var config = environment.keycloak;

    if (!config.url || !config.realm || !config.clientID) {
      console.error('[keycloakProvider] Incomplete Keycloak config:', config);
      initState = 'failed';
      deferred.reject(new Error('Incomplete Keycloak configuration'));
      return deferred.promise;
    }

    // Lazy load keycloak.js script
    lazyLoadScriptService.loadScript('/assets/scripts/keycloak.js')
      .then(function() {
        if (!$window.Keycloak) {
          throw new Error('window.Keycloak not set after loading script. Check that build:keycloak ran successfully.');
        }

        keycloak = new $window.Keycloak({
          url: config.url,
          realm: config.realm,
          clientId: config.clientID,
        });

        _setupKeycloakLifecycleHandlers();

        var onLoad = options.forceLogin ? 'login-required' : 'check-sso';

        return keycloak.init({
          onLoad: onLoad,
          checkLoginIframe: false,
          pkceMethod: 'S256',
          flow: 'standard',
          silentCheckSsoFallback: false,
        });
      })
      .then(function(authenticated) {
        keycloakReady = true;

        return _updateAuthenticationState().then(function() {
          return authenticated;
        });
      })
      .then(function(authenticated) {
        if (authenticated) {
          _startProactiveTokenRefresh();
        }

        initState = 'ready';
        deferred.resolve(authenticated);
      })
      .catch(function(error) {
        _reportFailure('[keycloakProvider] Initialization failed', error);
        keycloakReady = true;
        initState = 'failed';
        deferred.reject(error);
      });

    return deferred.promise;
  };

  service.isAuthenticated = function() {
    var deferred = $q.defer();

    if (!keycloakReady) {
      deferred.reject(new Error('Keycloak not initialized'));
      return deferred.promise;
    }

    deferred.resolve(authState.isAuthenticated);
    return deferred.promise;
  };

  service.user = function() {
    var deferred = $q.defer();

    if (!keycloakReady) {
      deferred.reject(new Error('Keycloak not initialized'));
      return deferred.promise;
    }

    deferred.resolve(authState.userProfile);
    return deferred.promise;
  };

  /**
   * Get fresh access token, refreshing if necessary.
   * If initialization is still pending, waits for it to complete.
   */
  service.getAccessToken = function() {
    if (initState === 'failed') {
      return $q.reject(new Error('Keycloak initialization failed'));
    }

    if (initState === 'pending' && initPromise) {
      return initPromise.then(function() {
        return _getTokenInternal();
      });
    }

    if (initState === 'ready') {
      return _getTokenInternal();
    }

    return $q.reject(new Error('Keycloak in unknown state'));
  };

  service.login = function(redirectUrl) {
    if (!keycloak) {
      console.error('[keycloakProvider] login called before init');
      return;
    }

    _attemptLogin({
      redirectUri: $window.location.origin + redirectUrl,
    });
  };

  service.logout = function() {
    if (!keycloak) {
      return;
    }

    if (refreshIntervalHandle) {
      $interval.cancel(refreshIntervalHandle);
      refreshIntervalHandle = null;
    }

    authState.isAuthenticated = false;
    authState.userProfile = null;
    authState.accessToken = null;

    $rootScope.$broadcast('keycloak:logout');

    keycloak.logout({
      redirectUri: $window.location.origin + '?logged_out=true',
    });
  };

  /**
   * Placeholder for OAuth callback handling (Auth0 compatibility).
   * Keycloak handles callbacks internally during init().
   */
  service.handleAuthentication = function() {
    var deferred = $q.defer();
    deferred.resolve(null);
    return deferred.promise;
  };

  // ========== Private Methods ==========

  /**
   * Normalize the assorted shapes that keycloak-js rejects with into something
   * a human can read in the console. Specifically covers:
   *   - `undefined` / `null`: keycloak-js discards the response body on token-exchange
   *     failures (keycloak.js:784) and onAuthRefreshError has no args (keycloak.js:674).
   *   - boolean `true`: keycloak-js rejects the refreshQueue promises with `true`
   *     (keycloak.js:676) when the refresh POST fails.
   * Without this, console errors read "Proactive token refresh failed: true" or
   * "Initialization failed: undefined", which are worse than useless for triage.
   */
  function _normalizeError(err) {
    if (err === undefined || err === null) {
      return '(no error details — keycloak-js discarded response body)';
    }
    if (typeof err === 'boolean') {
      return '(library rejected with boolean ' + String(err) + ' — response body discarded)';
    }
    if (err instanceof Error) {
      return { name: err.name, message: err.message, stack: err.stack };
    }
    if (typeof err === 'object') {
      try {
        return { type: err.constructor ? err.constructor.name : 'object', details: err };
      } catch (e) {
        return String(err);
      }
    }
    return String(err);
  }

  /**
   * Send a structured failure both to the dev console (for live debugging) and to
   * Sentry (for prod telemetry). Single call site so future tag/level adjustments
   * don't require touching 9 individual log sites.
   *
   * @param {string} tag - Sentry message + console.error first arg. Acts as Sentry's
   *   issue grouping key — keep stable per error class.
   * @param {*} error - Whatever was caught (boolean true, undefined, Error, etc).
   * @param {'warning'|'error'} [level] - Defaults to 'warning' since most keycloak
   *   failures self-recover; use 'error' for terminal states (bootstrap UI, breaker).
   */
  function _reportFailure(tag, error, level) {
    var normalized = _normalizeError(error);
    var state = _snapshotState();
    console.error(tag, normalized, state);
    MonitoringUtils.captureMessage(tag, {
      level: level || 'warning',
      extra: { error: normalized, state: state },
    });
  }

  /**
   * Snapshot of keycloak + browser state at the time of a failure. Paired with
   * `_normalizeError` at every error log site so that when a user copy/pastes
   * their console, we have enough to reason about WHAT was happening even
   * without the Keycloak response body.
   */
  function _snapshotState() {
    var nowSec = Math.floor(Date.now() / 1000);
    var tokenExp = keycloak && keycloak.tokenParsed && keycloak.tokenParsed.exp;
    var refreshExp = keycloak && keycloak.refreshTokenParsed && keycloak.refreshTokenParsed.exp;

    return {
      authenticated: !!(keycloak && keycloak.authenticated),
      hasToken: !!(keycloak && keycloak.token),
      hasRefreshToken: !!(keycloak && keycloak.refreshToken),
      tokenExpiresInSec: tokenExp ? tokenExp - nowSec : null,
      refreshTokenExpiresInSec: refreshExp ? refreshExp - nowSec : null,
      url: $window.location.href,
      online: navigator.onLine,
      visibility: document.visibilityState,
      loginAttempts: _readLoginAttempts().length,
    };
  }

  function _getTokenInternal() {
    var deferred = $q.defer();

    if (!keycloak) {
      deferred.reject(new Error('Keycloak not initialized'));
      return deferred.promise;
    }

    keycloak.updateToken(5)
      .then(function() {
        if (!keycloak.token) {
          throw new Error('No token available after refresh');
        }

        authState.accessToken = keycloak.token;
        deferred.resolve(keycloak.token);
      })
      .catch(function(error) {
        _reportFailure('[keycloakProvider] Failed to get access token', error);
        deferred.reject(error);
      });

    return deferred.promise;
  }

  function _setupKeycloakLifecycleHandlers() {
    if (!keycloak) {
      return;
    }

    keycloak.onReady = function(authenticated) {
      $rootScope.$broadcast('keycloak:ready', authenticated);
    };

    keycloak.onAuthSuccess = function() {
      // Successful auth → reset the circuit breaker (we're out of any prior loop).
      _clearLoginAttempts();

      _updateAuthenticationState();
      $rootScope.$broadcast('keycloak:authSuccess');
    };

    keycloak.onAuthError = function(errorData) {
      // Note: keycloak-js calls this handler with no args on token-exchange failure
      // (keycloak.js:784) — most of the time `errorData` will be undefined and the
      // real Keycloak response body is unrecoverable. Snapshot state instead.
      _reportFailure('[keycloakProvider] onAuthError', errorData);
      $rootScope.$broadcast('keycloak:authError', errorData);
    };

    keycloak.onAuthRefreshSuccess = function() {
      if (keycloak.token) {
        authState.accessToken = keycloak.token;
      }
      $rootScope.$broadcast('keycloak:tokenRefreshed');
    };

    keycloak.onAuthRefreshError = function() {
      // keycloak-js calls this handler with no args and has already cleared tokens
      // by the time we see it (keycloak.js:670-677). The state snapshot is still
      // useful for correlating wake-up vs expected expiry.
      _reportFailure('[keycloakProvider] onAuthRefreshError', undefined);

      authState.isAuthenticated = false;
      authState.userProfile = null;
      authState.accessToken = null;

      $rootScope.$broadcast('keycloak:tokenRefreshError');

      if (keycloak) {
        _handleAuthLost();
      }
    };

    keycloak.onAuthLogout = function() {
      authState.isAuthenticated = false;
      authState.userProfile = null;
      authState.accessToken = null;

      $rootScope.$broadcast('keycloak:logout');
    };

    keycloak.onTokenExpired = function() {
      console.warn('[keycloakProvider] onTokenExpired fired - attempting refresh');

      if (keycloak) {
        keycloak.updateToken(5).catch(function(error) {
          _reportFailure('[keycloakProvider] Token refresh failed after expiration', error);
        });
      }

      $rootScope.$broadcast('keycloak:tokenExpired');
    };
  }

  function _updateAuthenticationState() {
    var deferred = $q.defer();

    if (!keycloak) {
      deferred.reject(new Error('Keycloak not initialized'));
      return deferred.promise;
    }

    var isAuthenticated = !!keycloak.authenticated;

    if (!isAuthenticated) {
      authState.isAuthenticated = false;
      authState.userProfile = null;
      authState.accessToken = null;
      deferred.resolve();
      return deferred.promise;
    }

    keycloak.loadUserInfo()
      .then(function(profile) {
        authState.isAuthenticated = true;
        authState.userProfile = profile;

        if (keycloak.token) {
          authState.accessToken = keycloak.token;
        }

        $rootScope.$broadcast('keycloak:userProfileLoaded', profile);
        deferred.resolve();
      })
      .catch(function(error) {
        _reportFailure('[keycloakProvider] Failed to load user profile', error);

        authState.isAuthenticated = true;
        authState.userProfile = null;

        if (keycloak.token) {
          authState.accessToken = keycloak.token;
        }

        deferred.resolve();
      });

    return deferred.promise;
  }

  /**
   * Proactive token refresh: checks every 30s, refreshes if <70s remaining.
   */
  function _startProactiveTokenRefresh() {
    if (!keycloak) {
      return;
    }

    refreshIntervalHandle = $interval(function() {
      if (!keycloak) {
        return;
      }

      keycloak.updateToken(70).catch(function(error) {
        // Expected error shape here is boolean `true` (see keycloak.js:676) — the
        // library throws away the real rejection body, so normalizing is the best we can do.
        _reportFailure('[keycloakProvider] Proactive token refresh failed', error);
      });
    }, 30000);

    $rootScope.$on('$destroy', function() {
      if (refreshIntervalHandle) {
        $interval.cancel(refreshIntervalHandle);
        refreshIntervalHandle = null;
      }
    });
  }

  // ========== Auth-Lost Recovery ==========

  /**
   * Recover from mid-session auth loss without bouncing the user through Keycloak.
   *
   * 1. Defer if tab hidden — avoids surprising the user with a Keycloak page on wake.
   * 2. Defer if offline — avoids chrome-error://chromewebdata/ mid-redirect.
   * 3. Try one silent refresh — leverages Keycloak's `Refresh Token Max Reuse`
   *    grace window: if the failure was a race with a concurrent refresh, a retry
   *    within the window can succeed.
   * 4. On retry failure, fall through to _attemptLogin (which has its own circuit
   *    breaker as the final safeguard).
   */
  function _handleAuthLost() {
    if (_authLostHandling) {
      return;
    }
    _authLostHandling = true;

    if (document.hidden) {
      console.warn('[keycloakProvider] auth-lost recovery deferred: tab hidden');
      var onVisibility = function() {
        if (document.hidden) {
          return;
        }
        document.removeEventListener('visibilitychange', onVisibility);
        _authLostHandling = false;
        _handleAuthLost();
      };
      document.addEventListener('visibilitychange', onVisibility);
      return;
    }

    if (!navigator.onLine) {
      console.warn('[keycloakProvider] auth-lost recovery deferred: offline');
      var onOnline = function() {
        $window.removeEventListener('online', onOnline);
        _authLostHandling = false;
        _handleAuthLost();
      };
      $window.addEventListener('online', onOnline);
      return;
    }

    if (!keycloak) {
      _authLostHandling = false;
      return;
    }

    keycloak.updateToken(5).then(function() {
      console.info('[keycloakProvider] recovered via silent refresh');
      _authLostHandling = false;
      // onAuthRefreshSuccess fires → authState updated upstream
    }).catch(function(error) {
      _reportFailure('[keycloakProvider] silent recovery failed; falling back to login redirect', error);
      _authLostHandling = false;
      _attemptLogin();
    });
  }

  // ========== Login Circuit Breaker ==========

  /**
   * Attempt a Keycloak login redirect, guarded by the circuit breaker.
   * Tracks attempts in sessionStorage (survives reloads triggered by SAML round-trips).
   * If LOGIN_ATTEMPTS_MAX attempts happen within LOGIN_ATTEMPTS_WINDOW_MS, suppresses
   * further redirects and surfaces a "session ended" growl notification.
   */
  function _attemptLogin(loginOptions) {
    if (!keycloak) {
      return false;
    }

    var now = Date.now();
    var attempts = _readLoginAttempts().filter(function(t) {
      return now - t < LOGIN_ATTEMPTS_WINDOW_MS;
    });

    if (attempts.length >= LOGIN_ATTEMPTS_MAX) {
      // Terminal state — user is stuck in a loop and we've shown them the manual
      // recovery prompt. `error` level so this surfaces in Sentry's "final failure"
      // dashboards distinctly from the warning-level transient retry attempts above.
      _reportFailure(
        '[keycloakProvider] Login circuit breaker tripped',
        new Error('Login circuit breaker tripped after ' + attempts.length + ' attempts in ' + (LOGIN_ATTEMPTS_WINDOW_MS / 1000) + 's'),
        'error'
      );
      _showSessionExpiredNotification();
      $rootScope.$broadcast('keycloak:loginCircuitBreakerTripped', { attempts: attempts.length });
      return false;
    }

    attempts.push(now);
    _writeLoginAttempts(attempts);

    if (loginOptions) {
      keycloak.login(loginOptions);
    } else {
      keycloak.login();
    }

    return true;
  }

  function _readLoginAttempts() {
    try {
      var raw = $window.sessionStorage.getItem(LOGIN_ATTEMPTS_STORAGE_KEY);
      if (!raw) {
        return [];
      }
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function _writeLoginAttempts(attempts) {
    try {
      $window.sessionStorage.setItem(LOGIN_ATTEMPTS_STORAGE_KEY, JSON.stringify(attempts));
    } catch (e) {
      // sessionStorage might be disabled or full — circuit breaker degrades gracefully.
    }
  }

  function _clearLoginAttempts() {
    try {
      $window.sessionStorage.removeItem(LOGIN_ATTEMPTS_STORAGE_KEY);
    } catch (e) {
      // ignore
    }
  }

  function _showSessionExpiredNotification() {
    var growl;
    try {
      growl = $injector.get('growl');
    } catch (e) {
      console.warn('[keycloakProvider] growl service unavailable; falling back to alert.');
      $window.alert('Your session has expired. Please reload the page to sign back in.');
      return;
    }

    growl.error(
      'Your session has expired. <strong>Click here to reload</strong> and sign in again.',
      {
        ttl: 0,
        disableCountDown: true,
        onclick: function() {
          $window.location.reload();
        },
      }
    );
  }

  return service;
}]);
